import { SearchIcon } from './SearchIcon';

export type SearchTriggerVariant = 'band' | 'row';

export interface SearchTriggerProps {
  /** @default 'band' */
  variant?: SearchTriggerVariant;
  /** @default 'Search' — also the accessible name when the label is hidden. */
  label?: string;
  /** Show the text label. Defaults to true for everything but `band`. */
  showLabel?: boolean;
  /** Whether the search surface is open. Drives `aria-expanded` and the open styling. */
  expanded?: boolean;
  /** id of the surface this button controls. */
  controls?: string;
  onClick?: () => void;
  className?: string;
}

/**
 * The way into {@link GlobalSearch}. Search has no page of its own on a league site, so the button
 * is the whole entry point.
 *
 * `band` is icon-only and sits at the right end of the nav band, in the same visual family as the
 * burger and the PWA back button. `row` carries its label because it sits among labelled nav rows
 * and would read as decoration without one.
 *
 * The design's third variant, `bar` — a field-shaped button the page places above the ads column —
 * is deliberately not built (SC-1096 D8): the header band is the only entry point this ticket
 * ships.
 */
export function SearchTrigger({
  variant = 'band',
  label = 'Search',
  showLabel,
  expanded = false,
  controls,
  onClick,
  className,
}: SearchTriggerProps) {
  const withLabel = showLabel ?? variant !== 'band';
  const classNames = [
    'search-trigger',
    `search-trigger--${variant}`,
    expanded ? 'search-trigger--expanded' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={classNames}
      onClick={onClick}
      // Always set, so a screen reader hears the state whether or not the label is visible.
      aria-expanded={expanded}
      aria-controls={controls}
      aria-label={withLabel ? undefined : label}
    >
      <span className="search-trigger__icon">
        <SearchIcon />
      </span>
      {withLabel && <span className="search-trigger__label">{label}</span>}
    </button>
  );
}
